import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { ClientDetailDto } from '@lib/dto/client.dto'
import type {
  HcpCustomerDto,
  HcpCustomerListDto,
} from '@lib/dto/housecall-pro.dto'
import type { SiteDto } from '@lib/dto/site.dto'
import { clientHcpService } from '@/services/ClientHcpService'
import { clientKeys, housecallProKeys, siteKeys } from '@/hooks/queryKeys'

/** Fewer letters than this match too many of HCP's ~4,000 customers to read. */
export const HCP_SEARCH_MIN_LENGTH = 2

/**
 * HCP's customers matching a search, read live from HCP.
 *
 * Keeps the previous answer on screen while the next one loads, so the list
 * does not blank out on every keystroke. Not retried: a refused key should
 * say so once.
 */
export function useHcpCustomerSearch(
  query: string,
): UseQueryResult<HcpCustomerListDto> {
  const trimmed = query.trim()
  return useQuery({
    queryKey: housecallProKeys.customerSearch(trimmed),
    queryFn: ({ signal }) => clientHcpService.searchCustomers(trimmed, signal),
    enabled: trimmed.length >= HCP_SEARCH_MIN_LENGTH,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60_000,
  })
}

/** One HCP customer with its addresses, or nothing asked while `id` is null. */
export function useHcpCustomer(
  id: string | null,
): UseQueryResult<HcpCustomerDto> {
  return useQuery({
    queryKey: housecallProKeys.customer(id ?? ''),
    queryFn: () => clientHcpService.getCustomer(id ?? ''),
    enabled: id !== null,
    retry: false,
    staleTime: 30_000,
  })
}

/**
 * A client's link changed: its detail is the answer, its sites' links may
 * have been cleared with it, and every customer read live now names a
 * different linked client.
 */
function useApplyClientLink(): (client: ClientDetailDto) => void {
  const queryClient = useQueryClient()
  return (client) => {
    queryClient.setQueryData(clientKeys.detail(client.id), client)
    void queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: siteKeys.details() })
    void queryClient.invalidateQueries({
      queryKey: [...housecallProKeys.all, 'customers'],
    })
  }
}

interface LinkHcpCustomerVariables {
  clientId: string
  customerId: string | null
}

export function useLinkHcpCustomer(): UseMutationResult<
  ClientDetailDto,
  Error,
  LinkHcpCustomerVariables
> {
  const apply = useApplyClientLink()
  return useMutation({
    mutationFn: ({ clientId, customerId }: LinkHcpCustomerVariables) =>
      clientHcpService.linkCustomer(clientId, customerId),
    onSuccess: apply,
  })
}

/** Creates the customer in HCP's live account. Never retried. */
export function useCreateHcpCustomer(): UseMutationResult<
  ClientDetailDto,
  Error,
  string
> {
  const apply = useApplyClientLink()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clientId: string) => clientHcpService.createCustomer(clientId),
    onSuccess: apply,
    // The customer may already be linked when the address match fails. Refresh
    // the client so the dialog stops offering to create it again.
    onError: (_error, clientId) => {
      void queryClient.invalidateQueries({
        queryKey: clientKeys.detail(clientId),
      })
    },
  })
}

interface LinkHcpAddressVariables {
  siteId: string
  clientId: string
  addressId: string | null
}

/** A site's link changed: its client's detail lists it, and so does the site. */
function useApplySiteLink(): (site: SiteDto) => void {
  const queryClient = useQueryClient()
  return (site) => {
    void queryClient.invalidateQueries({
      queryKey: clientKeys.detail(site.clientId),
    })
    void queryClient.invalidateQueries({ queryKey: siteKeys.detail(site.id) })
  }
}

export function useLinkHcpAddress(): UseMutationResult<
  SiteDto,
  Error,
  LinkHcpAddressVariables
> {
  const apply = useApplySiteLink()
  return useMutation({
    mutationFn: ({ siteId, addressId }: LinkHcpAddressVariables) =>
      clientHcpService.linkAddress(siteId, addressId),
    onSuccess: apply,
  })
}

interface CreateHcpAddressVariables {
  siteId: string
  /** The customer the address is added to, whose addresses are then stale. */
  customerId: string
}

/** Adds the address in HCP's live account. Never retried. */
export function useCreateHcpAddress(): UseMutationResult<
  SiteDto,
  Error,
  CreateHcpAddressVariables
> {
  const queryClient = useQueryClient()
  const apply = useApplySiteLink()
  return useMutation({
    mutationFn: ({ siteId }: CreateHcpAddressVariables) =>
      clientHcpService.createAddress(siteId),
    onSuccess: (site, variables) => {
      apply(site)
      void queryClient.invalidateQueries({
        queryKey: housecallProKeys.customer(variables.customerId),
      })
    },
  })
}
